import { forwardRef } from 'react';
import type { HTMLAttributes, InputHTMLAttributes, SelectHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { theme } from './theme';

// Components that map cleanly to dz-shared-styles BEM classes (button,
// dropdown, tab-buttons, tile) are plain React wrappers below. Components
// without a shared equivalent (Chip, IconButton, Spinner, …) stay as
// Emotion styled blocks but read all their values from theme.ts which
// itself resolves to var(--…) tokens.

const cn = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

// ─── Shared-class wrappers ────────────────────────────────────────────────

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Card({ className, style, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('tile', 'tile--elevation-low', className)}
        // Preserve previous Card cosmetics: budget's Card has bottom margin
        // and a roomier padding than dz-shared-styles' default tile.
        style={{ padding: theme.spacing.lg, marginBottom: theme.spacing.lg, ...style }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

// Bare section wrapper for chart/table groups. Per the Figma dashboard
// layout, charts and the budget table sit flush on the page background —
// no card chrome (no tile classes, no shadow, no radius, no inner
// padding). We keep the marginBottom so the existing vertical rhythm
// between consecutive chart/table sections is preserved without relying
// on parent flex `gap`. Ref-forwarding mirrors Card so callers (e.g. the
// area-chart scroll-into-view) keep working.
export const Section = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function Section({ className, style, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={className}
        style={{ marginBottom: theme.spacing.lg, ...style }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

interface ButtonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 's' | 'm';
}
export const Button = forwardRef<HTMLDivElement, ButtonProps>(
  function Button({ variant = 'secondary', size = 'm', className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'button',
          `button--${variant}`,
          `button--size-${size}`,
          className,
        )}
        {...rest}
      />
    );
  },
);

// Native <select> already has `size` (visible-options count, number).
// Omit it so our string-typed `size` doesn't collide.
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: 's' | 'm';
}
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select({ size = 'm', className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cn('dropdown', `dropdown--size-${size}`, className)}
        {...rest}
      />
    );
  },
);

export const ToggleGroup = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function ToggleGroup({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('tab-buttons', 'tab-buttons--secondary', 'tab-buttons--size-s', className)}
        {...rest}
      />
    );
  },
);

interface ToggleButtonProps extends HTMLAttributes<HTMLDivElement> {
  active?: boolean;
}
export const ToggleButton = forwardRef<HTMLDivElement, ToggleButtonProps>(
  function ToggleButton({ active, className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'tab-buttons__button',
          active && 'tab-buttons__button--active',
          className,
        )}
        {...rest}
      />
    );
  },
);

// ─── Emotion-only primitives (no shared-styles equivalent) ────────────────

// Matches dz-shared-styles `@mixin serif-l`: 1.875rem GT-Alpina with -0.02em
// tracking. Used for table/section headlines (e.g. "Investitionsprojekte").
// Reproduces the mixin literally because shared exposes it as Sass only,
// not as a CSS class — the widget can't @include it from CSS-in-JS.
export const CardTitle = styled.h3`
  font-family: var(--serif-stack);
  font-size: 1.875rem;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: normal;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

// Sans-s body text for the small subtitle line under a CardTitle (e.g.
// "8 Einträge").
export const CardSubtitle = styled.p`
  font-family: var(--sans-stack);
  font-size: 1rem;
  line-height: 1.2;
  color: ${theme.colors.text.muted};
  margin: 0;
`;

export const FlexRow = styled.div<{ gap?: string; wrap?: boolean; align?: string; justify?: string }>`
  display: flex;
  flex-direction: row;
  gap: ${props => props.gap || theme.spacing.md};
  flex-wrap: ${props => props.wrap ? 'wrap' : 'nowrap'};
  align-items: ${props => props.align || 'center'};
  justify-content: ${props => props.justify || 'flex-start'};
`;

export const FlexCol = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.gap || theme.spacing.md};
`;

export const Input = styled.input<InputHTMLAttributes<HTMLInputElement>>`
  font-family: ${theme.fontFamilySans};
  font-size: ${theme.fontSize.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface};
  color: ${theme.colors.text.primary};
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

export const IconButton = styled.div<{ size?: 'sm' | 'md' | 'lg' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.size === 'sm' ? '4px' : props.size === 'lg' ? '12px' : '8px'};
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: ${theme.colors.text.primary};
  }
`;

// Icon-only action in a chart header (export, embed). Shared so buttons that
// sit next to each other cannot drift apart in padding, glyph size or hover
// treatment. Larger glyph and a primary-coloured hover distinguish it from the
// general-purpose IconButton above.
//
// It is a div because the host's shared styles own `button`, so keyboard access
// has to be added back explicitly: role, tab stop, Enter/Space, focus ring.
const ChartActionButtonBase = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  font-size: 18px;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: ${theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 1px;
  }
`;

export const ChartActionButton = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function ChartActionButton({ onKeyDown, ...rest }, ref) {
    return (
      <ChartActionButtonBase
        {...rest}
        ref={ref}
        role="button"
        tabIndex={0}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            (event.currentTarget as HTMLDivElement).click();
          }
          onKeyDown?.(event);
        }}
      />
    );
  },
);

export const Chip = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.xl};
  font-family: ${theme.fontFamilySans};
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  background: ${props => props.active ? 'var(--primary-color)' : theme.colors.background};
  color: ${props => props.active ? theme.colors.text.primary : theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? 'var(--color-data-700)' : 'var(--color-bg-muted)'};
  }
`;

export const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

export const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.border};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const ErrorMessage = styled.div`
  padding: ${theme.spacing.md};
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.error};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.sm};
`;

export const Divider = styled.div<{ vertical?: boolean }>`
  ${props => props.vertical ? `
    width: 1px;
    height: 24px;
    margin: 0 ${theme.spacing.sm};
  ` : `
    height: 1px;
    width: 100%;
    margin: ${theme.spacing.sm} 0;
  `}
  background: ${theme.colors.border};
`;
